
import { computed, defineComponent, onMounted, ref, reactive } from "vue";
import { mapGetters, useStore } from "vuex";
import i18n from "@/i18n";
import PromotionalVideo from "@/components/home/PromotionalVideo.vue";
import useBreakpoints from "@/services/Breakpoints";
import { useRouter } from "vue-router";

export default defineComponent({
  components: {
    PromotionalVideo,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const { type } = useBreakpoints();
    const optionalOnlineRegisterURL = ref("");
    const isOnlineRegisterActive = ref(true);
    const eventData = computed(() => {
      return store.getters["user/eventData"];
    });
    const isLogged = computed(() => {
      const statusPaid = store.getters["user/isLogged"];
      if (statusPaid) return store.getters["user/isPaid"];
      return false;
    });
    let counter = reactive({
      days: "00",
      hours: "00",
      minutes: "00",
      seconds: "00",
    });
    const isEventEnded = ref(false);

    function formatDateStart(date) {
      const months = {
        0: i18n.global.t("home.homeView.january"),
        1: i18n.global.t("home.homeView.february"),
        2: i18n.global.t("home.homeView.march"),
        3: i18n.global.t("home.homeView.april"),
        4: i18n.global.t("home.homeView.may"),
        5: i18n.global.t("home.homeView.june"),
        6: i18n.global.t("home.homeView.july"),
        7: i18n.global.t("home.homeView.august"),
        8: i18n.global.t("home.homeView.september"),
        9: i18n.global.t("home.homeView.october"),
        10: i18n.global.t("home.homeView.november"),
        11: i18n.global.t("home.homeView.december"),
      };
      if (date) {
        const d = new Date(date);
        return `${months[d.getMonth()]} ${d.getUTCDate()}, ${d.getFullYear()}`;
      }
      return "";
    }

    function goToRegister() {
      if (isOnlineRegisterActive.value)
        router.push({
          name: "Register",
          params: { eventID: eventData.value.id },
        });
      else if (optionalOnlineRegisterURL.value !== "")
        window.open(optionalOnlineRegisterURL.value, "_blank");
    }

    function eventLocation() {
      if (eventData.value) {
        if (eventData.value.footer && eventData.value.footer.eventLocation)
          return eventData.value.eventLocation;
        return i18n.global.t("home.noLocation");
      }
      return i18n.global.t("home.noLocation");
    }

    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : {
            r: 0,
            g: 0,
            b: 0,
          };
    };

    function getGradiantColor() {
      if (eventData.value) {
        if (eventData.value.themeSettings) {
          const primaryColor = eventData.value.themeSettings.primaryColor;
          return {
            "background-image": `linear-gradient(90deg, rgba(${
              hexToRgb(primaryColor).r - 80
            },${hexToRgb(primaryColor).g - 80},${
              hexToRgb(primaryColor).b - 80
            },1) 0%, rgba(${hexToRgb(primaryColor).r + 80},${
              hexToRgb(primaryColor).g + 80
            },${hexToRgb(primaryColor).b + 80},1) 100%)`,
            color: eventData.value.themeSettings.primaryTextColor,
          };
        }
      }
      return {};
    }

    function updateCountdown() {
      if (eventData.value) {
        const now = new Date();
        const startDate = new Date(eventData.value.startDate);
        if (now < startDate) {
          const milisc_seconds = 1000;
          const milisc_minutes = milisc_seconds * 60;
          const milisc_hours = milisc_minutes * 60;
          const milisc_day = milisc_hours * 24;
          const duration = Number(startDate) - Number(now);
          const remaining_days = Math.floor(duration / milisc_day);
          const remaining_hours = Math.floor(
            (duration % milisc_day) / milisc_hours
          );
          const remaining_minutes = Math.floor(
            (duration % milisc_hours) / milisc_minutes
          );
          const remaining_seconds = Math.floor(
            (duration % milisc_minutes) / milisc_seconds
          );
          counter.days = remaining_days.toString();
          counter.hours = remaining_hours.toString();
          counter.minutes = remaining_minutes.toString();
          counter.seconds = remaining_seconds.toString();
        } else {
          counter = {
            days: "00",
            hours: "00",
            minutes: "00",
            seconds: "00",
          };
        }
      }
    }

    function checkEndDate() {
      if (eventData.value) {
        const endDate = new Date(eventData.value.endDate);
        isEventEnded.value = endDate.getTime() < Date.now();
      }
    }
    function setRegistrationSettings() {
      const feature = eventData.value.eventFeatures.find(
        (feature) => feature.id === "online_registration"
      );
      if (feature) {
        isOnlineRegisterActive.value = feature.value;
        optionalOnlineRegisterURL.value = eventData.value
          .optionalOnlineRegisterURL
          ? eventData.value.optionalOnlineRegisterURL
          : "";
      }
    }

    onMounted(() => {
      setInterval(updateCountdown, 1000);
      checkEndDate();
      setRegistrationSettings();
    });

    return {
      isEventEnded,
      counter,
      type,
      eventData,
      isLogged,
      formatDateStart,
      goToRegister,
      eventLocation,
      getGradiantColor,
      hexToRgb,
      updateCountdown,
      checkEndDate,
      optionalOnlineRegisterURL,
      isOnlineRegisterActive,
    };
  },
});
